import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Save } from 'lucide-react';

interface EditPresentationVideoDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  video: {
    id: string;
    title: string;
    description: string | null;
    youtube_url: string;
    category: string;
    display_order: number;
  } | null;
}

const categories = ['features', 'tutorials', 'testimonials', 'news'] as const;

export const EditPresentationVideoDialog = ({ 
  isOpen, 
  onOpenChange, 
  onSuccess,
  video 
}: EditPresentationVideoDialogProps) => {
  const { t } = useTranslation('presentation');
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [category, setCategory] = useState<string>('features');
  const [displayOrder, setDisplayOrder] = useState(0);

  useEffect(() => {
    if (video) {
      setTitle(video.title);
      setDescription(video.description || '');
      setYoutubeUrl(video.youtube_url);
      setCategory(video.category);
      setDisplayOrder(video.display_order);
    }
  }, [video]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!video) return;
    
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('presentation_videos')
        .update({
          title,
          description: description || null,
          youtube_url: youtubeUrl,
          category,
          display_order: displayOrder,
        })
        .eq('id', video.id);

      if (error) throw error;

      toast({
        title: t('messages.videoUpdated'),
        description: t('messages.videoUpdatedDesc'),
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error updating video:', error);
      toast({
        title: t('messages.error'),
        description: t('messages.errorUpdating'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('editVideo.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-title">{t('form.videoTitle')}</Label>
            <Input
              id="edit-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-description">{t('form.description')}</Label>
            <Textarea
              id="edit-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-url">{t('form.youtubeUrl')}</Label>
            <Input
              id="edit-url"
              type="url"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-category">{t('form.category')}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {t(`categories.${cat}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="edit-order">{t('form.displayOrder')}</Label>
            <Input
              id="edit-order"
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
            />
          </div>
          <Button type="submit" className="w-full bg-golf-500 hover:bg-golf-600" disabled={isLoading}>
            <Save className="mr-2 h-4 w-4" />
            {isLoading ? t('form.saving') : t('form.save')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
